import { useState, useEffect } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { useSite, useJezPressSso, useRocketSso, useUpdateSite } from '../hooks/useSitesRegistry'
import { useSession } from '@/client/lib/auth'
import type { MaintenanceSite, SiteSource } from '../types'
import {
  ExternalLink,
  Globe,
  Rocket,
  Shield,
  Clock,
  FileText,
  LogIn,
  Loader2,
  Pencil,
  Save,
  X,
} from 'lucide-react'
import { format, formatDistanceToNow } from 'date-fns'
import { toast } from 'sonner'

interface SiteDrawerProps {
  siteId: string | null
  onClose: () => void
}

// Source badge colors and icons
const sourceConfig: Record<SiteSource, { label: string; variant: 'default' | 'secondary' | 'outline'; icon: typeof Globe }> = {
  rocketnet: { label: 'Rocket.net', variant: 'default', icon: Rocket },
  'auth-server': { label: 'JezPress', variant: 'secondary', icon: Shield },
  manual: { label: 'Manual', variant: 'outline', icon: FileText },
  'mcp-check': { label: 'MCP', variant: 'outline', icon: Clock },
  external: { label: 'External', variant: 'outline', icon: Globe },
}

function SourceBadge({ source }: { source: SiteSource }) {
  const config = sourceConfig[source] || sourceConfig.external
  const Icon = config.icon

  return (
    <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  )
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === 'active' ? 'default' : status === 'inactive' ? 'secondary' : 'outline'
  return <Badge variant={variant}>{status}</Badge>
}

function WpLoginButton({ site }: { site: MaintenanceSite }) {
  const { data: session } = useSession()
  const jezPressSso = useJezPressSso()
  const rocketSso = useRocketSso()

  const isLoading = jezPressSso.isPending || rocketSso.isPending
  const canSso = site.source === 'auth-server' || (site.source === 'rocketnet' && site.externalId)

  const handleClick = () => {
    if (site.source === 'auth-server' && session?.user?.email) {
      jezPressSso.mutate(
        { domain: site.domain, userEmail: session.user.email },
        {
          onSuccess: (data) => window.open(data.login_url, '_blank'),
          onError: (error) => {
            toast.error(error.message)
            window.open(`https://${site.domain}/wp-admin`, '_blank')
          },
        }
      )
    } else if (site.source === 'rocketnet' && site.externalId) {
      rocketSso.mutate(site.externalId, {
        onSuccess: (data) => window.open(data.login_url, '_blank'),
        onError: (error) => {
          toast.error(error.message)
          window.open(`https://${site.domain}/wp-admin`, '_blank')
        },
      })
    }
  }

  // No SSO available — plain link
  if (!canSso) {
    return (
      <Button asChild size="sm" variant="outline">
        <a href={`https://${site.domain}/wp-admin`} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="h-4 w-4 mr-1" />
          WP Admin
        </a>
      </Button>
    )
  }

  return (
    <Button size="sm" variant="outline" onClick={handleClick} disabled={isLoading}>
      {isLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <LogIn className="h-4 w-4 mr-1" />}
      WP Login
    </Button>
  )
}

export function SiteDrawer({ siteId, onClose }: SiteDrawerProps) {
  const { data: site, isLoading } = useSite(siteId || '')
  const updateSite = useUpdateSite()

  // Edit mode state
  const [editing, setEditing] = useState(false)
  const [editTags, setEditTags] = useState('')
  const [editNotes, setEditNotes] = useState('')
  const [editInterval, setEditInterval] = useState('')
  const [editNextDue, setEditNextDue] = useState('')

  // Reset edit state when site changes
  useEffect(() => {
    if (site) {
      setEditTags(site.tags?.join(', ') || '')
      setEditNotes(site.notes || '')
      setEditInterval(String(site.checkIntervalDays || 7))
      setEditNextDue(site.nextMaintenanceDue ? format(new Date(site.nextMaintenanceDue), 'yyyy-MM-dd') : '')
    }
    setEditing(false)
  }, [site])

  const handleSaveEdits = async () => {
    if (!site) return
    const tags = editTags
      .split(',')
      .map(t => t.trim())
      .filter(Boolean)

    try {
      await updateSite.mutateAsync({
        id: site.id,
        tags,
        notes: editNotes || null,
        checkIntervalDays: parseInt(editInterval, 10) || 7,
        nextMaintenanceDue: editNextDue ? new Date(editNextDue).getTime() : null,
      })
      setEditing(false)
      toast.success('Site updated')
    } catch (err) {
      toast.error('Failed to update site')
    }
  }

  return (
    <Sheet open={!!siteId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        {isLoading ? (
          <div className="space-y-4 pt-6">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : site ? (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-muted-foreground" />
                <span className="font-mono text-base">{site.domain}</span>
              </SheetTitle>
              <SheetDescription>
                {site.name || 'WordPress site'}
              </SheetDescription>
            </SheetHeader>

            <div className="px-4 pb-4 space-y-6">
              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2">
                <Button asChild size="sm" variant="outline">
                  <a
                    href={`https://${site.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Visit Site
                  </a>
                </Button>
                <WpLoginButton site={site} />
              </div>

              <Separator />

              {/* Site Info */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Site Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Source</p>
                    <div className="mt-1">
                      <SourceBadge source={site.source} />
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <div className="mt-1">
                      <StatusBadge status={site.status} />
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Added</p>
                    <p className="mt-1">
                      {formatDistanceToNow(new Date(site.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Last Updated</p>
                    <p className="mt-1">
                      {formatDistanceToNow(new Date(site.updatedAt), { addSuffix: true })}
                    </p>
                  </div>
                  {site.woocommerce && (
                    <div className="col-span-2">
                      <Badge variant="secondary">WooCommerce</Badge>
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-muted-foreground text-sm">Tags</p>
                    {!editing && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditing(true)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  {editing ? (
                    <Input
                      className="mt-1 text-sm"
                      placeholder="staging, woocommerce, priority"
                      value={editTags}
                      onChange={(e) => setEditTags(e.target.value)}
                    />
                  ) : site.tags && site.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {site.tags.map((tag: string) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-muted-foreground">No tags</p>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <p className="text-muted-foreground text-sm">Notes</p>
                  {editing ? (
                    <Textarea
                      className="mt-1 text-sm"
                      rows={3}
                      placeholder="Add notes about this site..."
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                    />
                  ) : site.notes ? (
                    <p className="mt-1 text-sm whitespace-pre-wrap">{site.notes}</p>
                  ) : (
                    <p className="mt-1 text-sm text-muted-foreground">No notes</p>
                  )}
                </div>
              </div>

              <Separator />
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Maintenance</h4>
                {editing ? (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="editInterval" className="text-xs text-muted-foreground">Check Interval (days)</Label>
                      <Input
                        id="editInterval"
                        type="number"
                        min="1"
                        className="mt-1"
                        value={editInterval}
                        onChange={(e) => setEditInterval(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="editNextDue" className="text-xs text-muted-foreground">Next Due</Label>
                      <Input
                        id="editNextDue"
                        type="date"
                        className="mt-1"
                        value={editNextDue}
                        onChange={(e) => setEditNextDue(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveEdits} disabled={updateSite.isPending}>
                        {updateSite.isPending ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-1" />
                        )}
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Interval</p>
                      <p className="mt-1">Every {site.checkIntervalDays || 7} days</p>
                    </div>
                    {site.lastMaintenanceAt && (
                      <div>
                        <p className="text-muted-foreground">Last Check</p>
                        <p className="mt-1">
                          {formatDistanceToNow(new Date(site.lastMaintenanceAt), { addSuffix: true })}
                        </p>
                      </div>
                    )}
                    {site.nextMaintenanceDue && (
                      <div>
                        <p className="text-muted-foreground">Next Due</p>
                        <p className="mt-1">
                          {formatDistanceToNow(new Date(site.nextMaintenanceDue), { addSuffix: true })}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="pt-6 text-center text-muted-foreground">
            Site not found
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
